import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Wallet, 
  TrendingUp, 
  Shield, 
  Zap, 
  ArrowRight,
  DollarSign,
  BarChart3,
  Globe,
  Users,
  CheckCircle,
  ChevronDown,
  ChevronUp,
  Star,
  Menu,
  X,
  Percent,
  Award
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { LiveMarketOverview, TradingViewChart, LiveTicker } from '@/components/MarketSection';
import { MarketDataProvider } from '@/context/MarketDataContext';

const fadeInUp = {
  initial: { opacity: 0, y: 30 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] }
};

const staggerContainer = {
  animate: {
    transition: {
      staggerChildren: 0.1,
      delayChildren: 0.1
    }
  }
};

const scaleIn = {
  initial: { opacity: 0, scale: 0.95 },
  animate: { opacity: 1, scale: 1 },
  transition: { duration: 0.5 }
};

export default function LandingPage() {
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [openFAQ, setOpenFAQ] = useState(null);

  // Lock body scroll when mobile menu is open
  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.classList.add('menu-open');
    } else {
      document.body.classList.remove('menu-open');
    }
    return () => document.body.classList.remove('menu-open');
  }, [mobileMenuOpen]);

  const closeMobileMenu = () => setMobileMenuOpen(false);

  const features = [
    {
      icon: TrendingUp,
      title: 'Daily Profits',
      description: 'Earn consistent daily returns of up to 2% on your investments with our proven trading strategies.'
    },
    {
      icon: Shield,
      title: 'Bank-Grade Security',
      description: 'Your assets are protected with enterprise-grade encryption and multi-layer security protocols.'
    },
    {
      icon: Zap,
      title: 'Instant Processing',
      description: 'Deposit and withdraw your funds in minutes with lightning-fast blockchain transactions.'
    },
    {
      icon: Globe,
      title: '24/7 Global Access',
      description: 'Access your portfolio from anywhere in the world, at any time, on any device.'
    },
  ];

  const stats = [
    { value: '$10M+', label: 'Trading Volume', icon: DollarSign },
    { value: '15,000+', label: 'Active Traders', icon: Users },
    { value: '2%', label: 'Daily Returns', icon: Percent },
    { value: '99.9%', label: 'Platform Uptime', icon: Award },
  ];

  const howItWorks = [
    { 
      step: '01', 
      title: 'Create Your Account', 
      description: 'Sign up in under 2 minutes with just your email. Verify your identity to unlock all features.',
      icon: Users
    },
    { 
      step: '02', 
      title: 'Fund Your Wallet', 
      description: 'Deposit BTC, ETH, or USDT to your secure Dexfolio wallet using our provided addresses.',
      icon: Wallet
    },
    { 
      step: '03', 
      title: 'Choose Trading Plan', 
      description: 'Select from our range of automated trading plans tailored to your goals.',
      icon: BarChart3
    },
    { 
      step: '04', 
      title: 'Earn Daily Profits', 
      description: 'Watch your portfolio grow with daily profit distributions from automated trading.',
      icon: TrendingUp
    },
  ];

  const testimonials = [
    {
      name: 'Michael Chen',
      role: 'Entrepreneur',
      content: 'Dexfolio has completely changed how I think about passive income. The daily returns are consistent and the platform is incredibly easy to use.',
      rating: 5,
      avatar: 'MC'
    },
    {
      name: 'Sarah Williams',
      role: 'Financial Analyst',
      content: 'As someone who works in finance, I was skeptical at first. But the transparency and security measures convinced me. Now I\'m a regular trader.',
      rating: 5,
      avatar: 'SW'
    },
    {
      name: 'David Okonkwo',
      role: 'Software Developer',
      content: 'The platform is smooth, withdrawals are fast, and the customer support is excellent. Highly recommend to anyone looking to grow their crypto holdings.',
      rating: 5,
      avatar: 'DO'
    },
  ];

  const faqs = [
    {
      question: 'How does Dexfolio generate daily returns?',
      answer: 'Dexfolio uses advanced algorithmic trading strategies, arbitrage opportunities, and DeFi yield farming to generate consistent returns. Our team of expert traders and AI systems work 24/7 to maximize profits while minimizing risk.'
    },
    {
      question: 'What is the minimum trading amount?',
      answer: 'You can start trading with as little as $10. There is no maximum limit, allowing you to trade according to your financial goals and comfort level.'
    },
    {
      question: 'How long does it take to withdraw funds?',
      answer: 'Withdrawal requests are processed within 24-48 hours. Once approved, the funds are sent directly to your specified wallet address. Blockchain confirmation times may vary depending on network congestion.'
    },
    {
      question: 'Is my capital secure?',
      answer: 'Absolutely. We employ bank-grade encryption, cold storage for the majority of funds, two-factor authentication, and regular security audits. Your assets are protected by industry-leading security protocols.'
    },
    {
      question: 'Can I track my profits in real-time?',
      answer: 'Yes! Our dashboard provides real-time tracking of your investments, daily profits, and complete transaction history. You can monitor your portfolio 24/7 from any device.'
    },
  ];

  const navLinks = [
    { href: '#features', label: 'Features' },
    { href: '#how-it-works', label: 'How It Works' },
    { href: '#testimonials', label: 'Testimonials' },
    { href: '#faq', label: 'FAQ' },
  ];

  return (
    <div className="min-h-screen overflow-x-hidden">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 px-4 pt-4" data-testid="landing-navbar">
        <div className="glass-card max-w-7xl mx-auto rounded-2xl">
          <div className="px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between h-16 sm:h-18">
              {/* Logo */}
              <Link to="/" className="flex items-center gap-2 shrink-0" data-testid="logo-link">
                <Wallet className="w-7 h-7 sm:w-8 sm:h-8 text-cyan-400" />
                <span className="text-xl sm:text-2xl font-outfit font-bold gradient-text">Dexfolio</span>
              </Link>
              
              {/* Desktop Navigation */}
              <div className="hidden lg:flex items-center gap-8">
                {navLinks.map((link) => (
                  <a 
                    key={link.href}
                    href={link.href} 
                    className="nav-link text-sm"
                  >
                    {link.label}
                  </a>
                ))}
              </div>

              {/* Desktop CTA Buttons */}
              <div className="hidden sm:flex items-center gap-3">
                <Button 
                  variant="ghost" 
                  onClick={() => navigate('/login')}
                  className="text-white/80 hover:text-white hover:bg-white/10"
                  data-testid="nav-login-btn"
                >
                  Login
                </Button>
                <Button 
                  className="btn-primary"
                  onClick={() => navigate('/register')}
                  data-testid="nav-get-started-btn"
                >
                  Get Started
                </Button>
              </div>

              {/* Mobile Menu Button */}
              <button
                className="lg:hidden p-2 rounded-lg hover:bg-white/10 transition-colors"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                aria-label="Toggle menu"
                data-testid="mobile-menu-toggle"
              >
                {mobileMenuOpen ? (
                  <X className="w-6 h-6 text-white" />
                ) : (
                  <Menu className="w-6 h-6 text-white" />
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Menu */}
        <AnimatePresence>
          {mobileMenuOpen && (
            <>
              {/* Backdrop */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40 lg:hidden"
                onClick={closeMobileMenu}
                data-testid="mobile-menu-backdrop"
              />
              
              {/* Menu Panel */}
              <motion.div
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.2 }}
                className="absolute top-full left-4 right-4 mt-2 glass-card rounded-2xl p-6 z-50 lg:hidden"
                data-testid="mobile-menu-panel"
              >
                <div className="flex flex-col gap-4">
                  {navLinks.map((link) => (
                    <a
                      key={link.href}
                      href={link.href}
                      className="text-white/80 hover:text-cyan-400 py-2 text-lg font-medium transition-colors"
                      onClick={closeMobileMenu}
                    >
                      {link.label}
                    </a>
                  ))}
                  <div className="border-t border-white/10 pt-4 mt-2 flex flex-col gap-3">
                    <Button 
                      variant="outline"
                      className="w-full btn-secondary"
                      onClick={() => { navigate('/login'); closeMobileMenu(); }}
                      data-testid="mobile-login-btn"
                    >
                      Login
                    </Button>
                    <Button 
                      className="w-full btn-primary"
                      onClick={() => { navigate('/register'); closeMobileMenu(); }}
                      data-testid="mobile-get-started-btn"
                    >
                      Get Started
                    </Button>
                  </div>
                </div>
              </motion.div>
            </>
          )}
        </AnimatePresence>
      </nav>

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden pt-24 pb-12 sm:pt-32 sm:pb-20" data-testid="hero-section">
        {/* Background */}
        <div className="absolute inset-0 z-0">
          <div className="absolute inset-0 bg-gradient-to-b from-cyan-500/5 via-transparent to-transparent" />
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-cyan-500/10 rounded-full blur-[128px]" />
          <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-emerald-500/10 rounded-full blur-[128px]" />
        </div>

        <div className="relative z-10 container-main text-center">
          <motion.div
            initial="initial"
            animate="animate"
            variants={staggerContainer}
            className="space-y-6 sm:space-y-8"
          >
            {/* Badge */}
            <motion.div variants={fadeInUp} className="inline-flex items-center gap-2 px-3 py-1.5 sm:px-4 sm:py-2 rounded-full bg-cyan-500/10 border border-cyan-500/30 text-cyan-400 text-xs sm:text-sm">
              <Zap className="w-3 h-3 sm:w-4 sm:h-4" />
              <span>Earn up to 2% daily returns</span>
            </motion.div>

            {/* Main Heading */}
            <motion.h1 
              variants={fadeInUp}
              className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-outfit font-bold tracking-tighter leading-tight"
            >
              <span className="text-white">Trade Smarter.</span>
              <br />
              <span className="gradient-text">Grow Faster.</span>
            </motion.h1>

            {/* Subheading */}
            <motion.p 
              variants={fadeInUp}
              className="text-base sm:text-lg md:text-xl text-white/70 max-w-2xl mx-auto px-4"
            >
              Join thousands of traders earning consistent daily returns through our 
              automated cryptocurrency trading platform. Start with as little as $10.
            </motion.p>

            {/* CTA Buttons */}
            <motion.div 
              variants={fadeInUp} 
              className="flex flex-col sm:flex-row items-center justify-center gap-4 px-4"
            >
              <Button 
                className="btn-primary text-base sm:text-lg px-6 sm:px-8 py-3 sm:py-4 h-auto w-full sm:w-auto touch-manipulation"
                onClick={() => navigate('/register')}
                data-testid="hero-get-started-btn"
              >
                Start Trading Now
                <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5 ml-2" />
              </Button>
              <Button 
                variant="outline"
                className="btn-secondary text-base sm:text-lg px-6 sm:px-8 py-3 sm:py-4 h-auto w-full sm:w-auto touch-manipulation"
                onClick={() => navigate('/login')}
                data-testid="hero-login-btn"
              >
                View Dashboard
              </Button>
            </motion.div>
          </motion.div>
        </div>

        {/* Scroll Indicator */}
        <motion.div 
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.5 }}
          className="absolute bottom-6 sm:bottom-8 left-1/2 -translate-x-1/2"
        >
          <div className="w-5 h-8 sm:w-6 sm:h-10 rounded-full border-2 border-white/30 flex items-start justify-center p-1.5 sm:p-2">
            <motion.div 
              animate={{ y: [0, 8, 0] }}
              transition={{ duration: 1.5, repeat: Infinity }}
              className="w-1 h-1 sm:w-1.5 sm:h-1.5 rounded-full bg-cyan-400"
            />
          </div>
        </motion.div>
      </section>

      {/* Market Data Section - Wrapped with shared data provider */}
      <MarketDataProvider>
        {/* Live Market Ticker */}
        <LiveTicker />

        {/* Market Overview Section */}
        <section className="section-padding relative" data-testid="market-section">
          <div className="container-main space-y-8">
            <motion.div 
              initial="initial"
              whileInView="animate"
              viewport={{ once: true, margin: "-100px" }}
              variants={staggerContainer}
              className="text-center mb-8"
            >
              <motion.p variants={fadeInUp} className="text-cyan-400 text-sm font-semibold uppercase tracking-wider mb-3">
                Real-Time Data
              </motion.p>
              <motion.h2 variants={fadeInUp} className="text-3xl sm:text-4xl font-outfit font-bold text-white mb-4">
                Live <span className="gradient-text">Market Prices</span>
              </motion.h2>
              <motion.p variants={fadeInUp} className="text-base sm:text-lg text-white/60 max-w-2xl mx-auto px-4">
                Track real-time cryptocurrency prices powered by CoinGecko
              </motion.p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <LiveMarketOverview />
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              <TradingViewChart defaultSymbol="BTC" />
            </motion.div>
          </div>
        </section>
      </MarketDataProvider>

      {/* Stats Section */}
      <section className="section-padding relative" data-testid="stats-section">
        <div className="container-main">
          <motion.div 
            initial="initial"
            whileInView="animate"
            viewport={{ once: true, margin: "-100px" }}
            variants={staggerContainer}
            className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6"
          >
            {stats.map((stat, index) => (
              <motion.div
                key={index}
                variants={fadeInUp}
                className="glass-card-hover p-4 sm:p-6 text-center"
              >
                <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-cyan-500/10 flex items-center justify-center mx-auto mb-3 sm:mb-4">
                  <stat.icon className="w-5 h-5 sm:w-6 sm:h-6 text-cyan-400" />
                </div>
                <p className="text-2xl sm:text-3xl lg:text-4xl font-outfit font-bold gradient-text mb-1 sm:mb-2">
                  {stat.value}
                </p>
                <p className="text-xs sm:text-sm text-white/50">{stat.label}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="section-padding relative" data-testid="features-section">
        <div className="container-main">
          <motion.div 
            initial="initial"
            whileInView="animate"
            viewport={{ once: true, margin: "-100px" }}
            variants={staggerContainer}
            className="text-center mb-12 sm:mb-16"
          >
            <motion.p variants={fadeInUp} className="text-cyan-400 text-sm font-semibold uppercase tracking-wider mb-3">
              Why Choose Us
            </motion.p>
            <motion.h2 variants={fadeInUp} className="text-3xl sm:text-4xl font-outfit font-bold text-white mb-4">
              Why Choose <span className="gradient-text">Dexfolio</span>?
            </motion.h2>
            <motion.p variants={fadeInUp} className="text-base sm:text-lg text-white/60 max-w-2xl mx-auto px-4">
              Experience the next generation of cryptocurrency investment with our cutting-edge platform.
            </motion.p>
          </motion.div>

          <motion.div 
            initial="initial"
            whileInView="animate"
            viewport={{ once: true, margin: "-100px" }}
            variants={staggerContainer}
            className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6"
          >
            {features.map((feature, index) => (
              <motion.div
                key={index}
                variants={fadeInUp}
                className="glass-card-hover p-5 sm:p-6 group"
              >
                <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-xl bg-gradient-to-br from-cyan-500/20 to-emerald-500/20 flex items-center justify-center mb-4 sm:mb-5 group-hover:scale-110 transition-transform duration-300">
                  <feature.icon className="w-6 h-6 sm:w-7 sm:h-7 text-cyan-400" />
                </div>
                <h3 className="text-lg sm:text-xl font-outfit font-semibold text-white mb-2">
                  {feature.title}
                </h3>
                <p className="text-white/60 text-sm leading-relaxed">
                  {feature.description}
                </p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* How It Works Section */}
      <section id="how-it-works" className="section-padding relative" data-testid="how-it-works-section">
        <div className="container-main">
          <motion.div 
            initial="initial"
            whileInView="animate"
            viewport={{ once: true, margin: "-100px" }}
            variants={staggerContainer}
            className="text-center mb-12 sm:mb-16"
          >
            <motion.p variants={fadeInUp} className="text-cyan-400 text-sm font-semibold uppercase tracking-wider mb-3">
              Getting Started
            </motion.p>
            <motion.h2 variants={fadeInUp} className="text-3xl sm:text-4xl font-outfit font-bold text-white mb-4">
              How It <span className="gradient-text">Works</span>
            </motion.h2>
            <motion.p variants={fadeInUp} className="text-base sm:text-lg text-white/60 max-w-2xl mx-auto px-4">
              Get started in just four simple steps and begin earning daily returns
            </motion.p>
          </motion.div>

          <motion.div 
            initial="initial"
            whileInView="animate"
            viewport={{ once: true, margin: "-100px" }}
            variants={staggerContainer}
            className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8"
          >
            {howItWorks.map((item, index) => (
              <motion.div
                key={index}
                variants={fadeInUp}
                className="relative"
              >
                <div className="glass-card-hover p-6 sm:p-8 text-center h-full">
                  <div className="text-5xl sm:text-6xl font-outfit font-bold text-cyan-500/10 mb-3 sm:mb-4">
                    {item.step}
                  </div>
                  <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-full bg-cyan-500/10 border border-cyan-500/30 flex items-center justify-center mx-auto mb-4 sm:mb-5">
                    <item.icon className="w-7 h-7 sm:w-8 sm:h-8 text-cyan-400" />
                  </div>
                  <h3 className="text-lg sm:text-xl font-outfit font-semibold text-white mb-2 sm:mb-3">
                    {item.title}
                  </h3>
                  <p className="text-white/60 text-sm leading-relaxed">
                    {item.description}
                  </p>
                </div>
                {/* Connector Line - Desktop Only */}
                {index < 3 && (
                  <div className="hidden lg:block absolute top-1/2 -right-4 transform -translate-y-1/2 z-10">
                    <ArrowRight className="w-8 h-8 text-cyan-500/30" />
                  </div>
                )}
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section id="testimonials" className="section-padding relative" data-testid="testimonials-section">
        <div className="container-main">
          <motion.div 
            initial="initial"
            whileInView="animate"
            viewport={{ once: true, margin: "-100px" }}
            variants={staggerContainer}
            className="text-center mb-12 sm:mb-16"
          >
            <motion.p variants={fadeInUp} className="text-cyan-400 text-sm font-semibold uppercase tracking-wider mb-3">
              Testimonials
            </motion.p>
            <motion.h2 variants={fadeInUp} className="text-3xl sm:text-4xl font-outfit font-bold text-white mb-4">
              What Our <span className="gradient-text">Traders</span> Say
            </motion.h2>
            <motion.p variants={fadeInUp} className="text-base sm:text-lg text-white/60 max-w-2xl mx-auto px-4">
              Don't just take our word for it - hear from our satisfied traders
            </motion.p>
          </motion.div>

          <motion.div 
            initial="initial"
            whileInView="animate"
            viewport={{ once: true, margin: "-100px" }}
            variants={staggerContainer}
            className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6"
          >
            {testimonials.map((testimonial, index) => (
              <motion.div
                key={index}
                variants={fadeInUp}
                className="glass-card-hover p-6 sm:p-8"
              >
                {/* Stars */}
                <div className="flex gap-1 mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} className="w-4 h-4 sm:w-5 sm:h-5 text-yellow-500 fill-yellow-500" />
                  ))}
                </div>
                
                {/* Quote */}
                <p className="text-white/80 text-sm sm:text-base leading-relaxed mb-6">
                  "{testimonial.content}"
                </p>
                
                {/* Author */}
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-gradient-to-br from-cyan-500 to-emerald-500 flex items-center justify-center text-sm sm:text-base font-bold text-white">
                    {testimonial.avatar}
                  </div>
                  <div>
                    <p className="text-white font-semibold text-sm sm:text-base">{testimonial.name}</p>
                    <p className="text-white/50 text-xs sm:text-sm">{testimonial.role}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="section-padding relative" data-testid="faq-section">
        <div className="container-narrow">
          <motion.div 
            initial="initial"
            whileInView="animate"
            viewport={{ once: true, margin: "-100px" }}
            variants={staggerContainer}
            className="text-center mb-12 sm:mb-16"
          >
            <motion.p variants={fadeInUp} className="text-cyan-400 text-sm font-semibold uppercase tracking-wider mb-3">
              FAQ
            </motion.p>
            <motion.h2 variants={fadeInUp} className="text-3xl sm:text-4xl font-outfit font-bold text-white mb-4">
              Frequently Asked <span className="gradient-text">Questions</span>
            </motion.h2>
            <motion.p variants={fadeInUp} className="text-base sm:text-lg text-white/60 max-w-2xl mx-auto px-4">
              Find answers to common questions about Dexfolio
            </motion.p>
          </motion.div>

          <motion.div 
            initial="initial"
            whileInView="animate"
            viewport={{ once: true, margin: "-100px" }}
            variants={staggerContainer}
            className="space-y-4"
          >
            {faqs.map((faq, index) => (
              <motion.div
                key={index}
                variants={fadeInUp}
                className="faq-item"
              >
                <button
                  onClick={() => setOpenFAQ(openFAQ === index ? null : index)}
                  className="faq-question"
                  data-testid={`faq-question-${index}`}
                >
                  <span className="text-sm sm:text-base pr-4">{faq.question}</span>
                  {openFAQ === index ? (
                    <ChevronUp className="w-5 h-5 text-cyan-400 shrink-0" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-white/50 shrink-0" />
                  )}
                </button>
                <AnimatePresence>
                  {openFAQ === index && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3 }}
                      className="overflow-hidden"
                    >
                      <div className="faq-answer">
                        {faq.answer}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="section-padding relative" data-testid="cta-section">
        <div className="container-main">
          <motion.div 
            initial="initial"
            whileInView="animate"
            viewport={{ once: true, margin: "-100px" }}
            variants={scaleIn}
            className="relative overflow-hidden rounded-3xl"
          >
            {/* Background Glow */}
            <div className="absolute inset-0 bg-gradient-to-r from-cyan-500/20 via-transparent to-emerald-500/20" />
            <div className="absolute top-0 left-1/4 w-96 h-96 bg-cyan-500/20 rounded-full blur-[128px]" />
            <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-emerald-500/20 rounded-full blur-[128px]" />
            
            <div className="relative glass-card p-8 sm:p-12 lg:p-16 text-center">
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-outfit font-bold text-white mb-4 sm:mb-6">
                Ready to Start Your <span className="gradient-text">Trading Journey</span>?
              </h2>
              <p className="text-base sm:text-lg text-white/60 mb-8 max-w-2xl mx-auto">
                Join thousands of traders already earning daily returns with Dexfolio. 
                Create your free account today and start growing your wealth.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <Button 
                  className="btn-primary text-base sm:text-lg px-8 sm:px-12 py-4 h-auto w-full sm:w-auto touch-manipulation"
                  onClick={() => navigate('/register')}
                  data-testid="cta-register-btn"
                >
                  Create Free Account
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
                <div className="flex items-center gap-2 text-white/50">
                  <CheckCircle className="w-5 h-5 text-emerald-400" />
                  <span className="text-sm">No credit card required</span>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 sm:py-12 border-t border-white/10" data-testid="landing-footer">
        <div className="container-main">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            {/* Logo */}
            <div className="flex items-center gap-2">
              <Wallet className="w-5 h-5 sm:w-6 sm:h-6 text-cyan-400" />
              <span className="text-base sm:text-lg font-outfit font-bold text-white">Dexfolio</span>
            </div>
            
            {/* Copyright */}
            <p className="text-xs sm:text-sm text-white/50 text-center">
              © {new Date().getFullYear()} Dexfolio. All rights reserved.
            </p>
            
            {/* Links */}
            <div className="flex items-center gap-4 sm:gap-6">
              <a href="#" className="text-xs sm:text-sm text-white/50 hover:text-cyan-400 transition-colors">Terms</a>
              <a href="#" className="text-xs sm:text-sm text-white/50 hover:text-cyan-400 transition-colors">Privacy</a>
              <a href="#" className="text-xs sm:text-sm text-white/50 hover:text-cyan-400 transition-colors">Support</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
